import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { apiClient } from '@/api/client'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { AuditFeed } from './AuditFeed'
import { MaintainerHome } from './MaintainerHome'
import { EmptyNote, Section } from './shared'
import type { Role } from '@/api/types'
import type { useTaskBuckets } from './useTaskBuckets'

type Buckets = ReturnType<typeof useTaskBuckets>

const ROLES: Role[] = ['admin', 'maintainer', 'radiologist', 'technician', 'developer', 'qa']

/**
 * Admin home = the maintainer sections plus the two things only an admin has:
 * who is in the system, and what everyone has been doing.
 *
 * Admin holds every permission (`permissions_for` is a hard bypass), so nothing
 * extra needs gating here — but `MaintainerHome` still gates its own queries on
 * `can(...)`, which is what lets one component serve both roles.
 */
export function AdminHome({ buckets }: { buckets: Buckets }) {
  return (
    <>
      <MaintainerHome buckets={buckets} />
      <UserRoleCounts />
      <Section
        title="Recent activity across the platform"
        description="The newest entries in the audit log — every security- and compliance-relevant action."
        action={
          <Button asChild variant="outline" size="sm">
            <Link to="/audit">Open audit log</Link>
          </Button>
        }
      >
        <AuditFeed emptyCopy="The audit log is empty. It fills as people log in, start studies, submit annotations and record verdicts." />
      </Section>
    </>
  )
}

function UserRoleCounts() {
  // `GET /users` returns every user in one call, so one query and a client-side
  // tally — six per-role requests would be six round trips for one number each.
  const { data, isLoading } = useQuery({
    queryKey: ['users', undefined],
    queryFn: () => apiClient.listUsers(),
  })

  const byRole = new Map<Role, number>()
  for (const u of data ?? []) byRole.set(u.role, (byRole.get(u.role) ?? 0) + 1)

  return (
    <Section
      title="People"
      description="Roles come from Keycloak and are mirrored onto every user on each login — change them there, not here."
    >
      {isLoading ? (
        <Card>
          <CardContent className="grid gap-4 p-4 sm:grid-cols-3 lg:grid-cols-6">
            {ROLES.map((r) => (
              <Skeleton key={r} className="h-12 w-full" />
            ))}
          </CardContent>
        </Card>
      ) : (data?.length ?? 0) === 0 ? (
        <EmptyNote>
          No users have signed in yet. A user row is created on first login through Keycloak — the
          platform has no separate invite step.
        </EmptyNote>
      ) : (
        <Card>
          <CardContent className="grid gap-4 p-4 sm:grid-cols-3 lg:grid-cols-6">
            {ROLES.map((r) => (
              <div key={r}>
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  {r}
                </p>
                <p className="mt-1 font-mono text-h3 font-semibold tabular-nums">
                  {byRole.get(r) ?? 0}
                </p>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </Section>
  )
}
